import { useState, useMemo, memo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Input } from '@ds';
import { AdminFormRow, AdminFormSection, ConditionalRender } from '@ui';
import { EmployeeFormValues } from '@/types';
import { EmployeeRole, LocationPreference, TrackedLocation } from '@/api/apis/resources';
import { Hub } from '@/api/apis/facilities';
import { getLocationAddress, getShortTzByIanaName } from '@/utils/helpers';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import { LocationTrackingInfo } from './LocationTrackingInfo';
import { HomeLocationFields } from './HomeLocationFields';
import { BaseLocationFields } from './BaseLocationFields';

type LocationInformationSectionProps = {
  isReadOnly?: boolean;
  trackedLocation?: TrackedLocation;
  baseLocation?: Hub;
  // Contractor-only props
  isContractor?: boolean;
  initialAirportName?: string;
  affiliationDisplayTimezone?: string;
};

export const LocationInformationSection = memo(
  ({
    isReadOnly,
    trackedLocation,
    baseLocation,
    isContractor,
    initialAirportName,
    affiliationDisplayTimezone,
  }: LocationInformationSectionProps) => {
    const { resolveSubdivisionName } = useCountriesData();
    const { control } = useFormContext<EmployeeFormValues>();
    const [roles, locationPreference, homeLocationTimezone, currentLocation] = useWatch({
      control,
      name: ['roles', 'locationPreference', 'homeLocationTimezone', 'currentLocation'],
    });

    const [activeBaseTimezone, setActiveBaseTimezone] = useState<string>(baseLocation?.location.timezone || '');

    const isSpecialist = roles?.includes(EmployeeRole.ClinicalSpecialist);
    const isRetrievalSurgeon = roles?.includes(EmployeeRole.RetrievalSurgeon);
    const isClinicalLead = roles?.includes(EmployeeRole.ClinicalLead);

    const isBaseLocationAndPreferencesShown = isContractor || isSpecialist || isRetrievalSurgeon || isClinicalLead;
    const isLocationPreferencesBase = locationPreference === LocationPreference.Base;
    const isBaseLocationRequired = isContractor ? isLocationPreferencesBase : isBaseLocationAndPreferencesShown;

    const isTimezoneToClear = isBaseLocationAndPreferencesShown && !isLocationPreferencesBase;

    const displayTimezone = useMemo(() => {
      const homeTz = getShortTzByIanaName(homeLocationTimezone || '');
      if (!isBaseLocationAndPreferencesShown) return homeTz;
      switch (locationPreference) {
        case LocationPreference.Base:
          return getShortTzByIanaName(activeBaseTimezone);
        case LocationPreference.Affiliation:
          return affiliationDisplayTimezone;
        default:
          return homeTz;
      }
    }, [
      isBaseLocationAndPreferencesShown,
      locationPreference,
      activeBaseTimezone,
      homeLocationTimezone,
      affiliationDisplayTimezone,
    ]);

    return (
      <AdminFormSection title="Location Information" isReadOnly>
        {isContractor ? (
          <AdminFormRow title="Current Location">
            <Input
              aria-label="Current Location"
              type="text"
              isReadOnly
              isDisabled
              value={getLocationAddress(currentLocation, resolveSubdivisionName)}
              placeholder="N/A"
            />
          </AdminFormRow>
        ) : (
          <LocationTrackingInfo trackedLocation={trackedLocation} />
        )}

        <HomeLocationFields
          isReadOnly={isReadOnly}
          isTimezoneToClear={isTimezoneToClear}
          isBaseLocationAndPreferencesShown={isBaseLocationAndPreferencesShown}
        />

        <ConditionalRender show={isBaseLocationAndPreferencesShown}>
          <BaseLocationFields
            isReadOnly={isReadOnly}
            isBaseLocationRequired={isBaseLocationRequired}
            onBaseLocationTimezoneChange={setActiveBaseTimezone}
            isContractor={isContractor}
            initialAirportName={initialAirportName}
          />
        </ConditionalRender>

        <AdminFormRow title="Timezone" isRequired>
          <Input aria-label="Timezone" value={displayTimezone || ''} isReadOnly isDisabled />
        </AdminFormRow>
      </AdminFormSection>
    );
  },
);
